import { useQuery } from '@tanstack/react-query';
import { useSession, signIn } from 'next-auth/react';
import { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';

interface GitHubScopeValidation {
  hasRequiredScopes: boolean;
  scopes: string[];
  isLoading: boolean;
  error: Error | null;
}

interface UseGitHubPermissionsOptions {
  requiredScopes?: string[];
  enabled?: boolean;
}

export const useGitHubPermissions = (
  options: UseGitHubPermissionsOptions = {}
) => {
  // GitHub repository data is read server-side on the PR-bot installation token,
  // so the login grant no longer includes `repo` and nothing here should demand
  // it. `read:org` is the only scope a client flow still depends on.
  const { requiredScopes = ['read:org'], enabled = true } = options;
  const { data: session } = useSession();
  const router = useRouter();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const validateGitHubScopes = async (): Promise<GitHubScopeValidation> => {
    if (!session?.user?.accessToken) {
      throw new Error('No access token available');
    }

    if (session.user.provider !== 'github') {
      return {
        hasRequiredScopes: true,
        scopes: [],
        isLoading: false,
        error: null,
      };
    }

    try {
      const response = await fetch('https://api.github.com/user', {
        headers: {
          Authorization: `token ${session.user.accessToken}`,
          'User-Agent': 'Entelligence-App',
        },
      });

      if (!response.ok) {
        throw new Error(
          `GitHub API request failed: ${response.status} ${response.statusText}`
        );
      }

      const scopesHeader = response.headers.get('X-OAuth-Scopes');
      const scopes = scopesHeader
        ? scopesHeader.split(',').map((s) => s.trim())
        : [];
      const hasRequiredScopes = requiredScopes.every((scope) =>
        scopes.includes(scope)
      );

      return {
        hasRequiredScopes,
        scopes,
        isLoading: false,
        error: null,
      };
    } catch (error) {
      throw error;
    }
  };

  const {
    data: validationResult,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'github-permissions',
      session?.user?.accessToken,
      requiredScopes,
    ],
    queryFn: validateGitHubScopes,
    enabled:
      enabled &&
      !!session?.user?.accessToken &&
      session?.user?.provider === 'github',
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: (failureCount, error: Error) => {
      return failureCount < 2;
    },
  });

  const requestPermissions = useCallback(
    async (callbackUrl?: string) => {
      if (isRedirecting || !session?.user?.provider) {
        return;
      }

      setIsRedirecting(true);

      try {
        const currentUrl =
          callbackUrl || window.location.pathname + window.location.search;

        // Re-authenticate directly to request all scopes
        await signIn(
          session.user.provider,
          {
            redirect: true,
            callbackUrl: currentUrl,
          },
          { prompt: 'consent' }
        );
      } catch (error) {
        setIsRedirecting(false);
      }
    },
    [isRedirecting, session?.user?.provider]
  );

  const validateAndProceed = useCallback(
    async (
      callback: () => void | Promise<void>,
      options?: {
        showModal?: boolean;
        callbackUrl?: string;
        customMessage?: string;
      }
    ) => {
      const { showModal = true, callbackUrl, customMessage } = options || {};

      if (session?.user?.provider !== 'github') {
        await callback();
        return;
      }

      if (isLoading) {
        return;
      }

      if (error) {
        return;
      }

      if (validationResult?.hasRequiredScopes) {
        await callback();
        return;
      }

      if (showModal) {
        const missingScopes = requiredScopes.filter(
          (scope) => !validationResult?.scopes?.includes(scope)
        );

        // Wait a bit for user to read the message
        setTimeout(() => {
          requestPermissions(callbackUrl);
        }, 1000);
      } else {
        await requestPermissions(callbackUrl);
      }
    },
    [
      validationResult,
      isLoading,
      error,
      requiredScopes,
      requestPermissions,
      session?.user?.provider,
    ]
  );

  return {
    hasRequiredScopes: validationResult?.hasRequiredScopes ?? false,
    scopes: validationResult?.scopes ?? [],
    isLoading,
    error,
    validateAndProceed,
    requestPermissions,
    refetch,
  };
};
